/**
 * Linking a Foundation Data v2 figure to an answer BY HAND - the answer editor's "Add foundation data" button.
 *
 * The AI links a figure by writing a `source_slices` entry (backend-python `foundation_entries`). A person links
 * one by inserting an inline citation:
 *
 *   <cite title="{dataKey}" data-foundation_uid=".." data-foundation_version=".." data-foundation_year=".."
 *         data-curated_point_id=".." data-foundation_figure="59,721 tCO2e">59,721 tCO2e [N]</cite>
 *
 * The AI writes exactly the same tag (backend-python `foundation_cite_html`) - keep the two in step. The figure is
 * also an attribute so renumbering can rebuild the text without losing it.
 *
 * and on save each such marker becomes the same slice-shaped entry the AI writes, so the usage badge, the
 * changed-figure notice and the Sources list need nothing of their own. The marker is the source of truth for
 * these links: delete it and the link goes on the next save. Entries flagged `citedInline` are owned by the
 * markers; every other entry (files, Q&A, and figures the AI cited without a marker) is left alone.
 *
 * Pure functions in their own file: `src/utils/index.js` contains JSX so vitest cannot import the barrel.
 */
import { v5 as uuidv5 } from "uuid";
import { CITE_ATTR_RE, CITE_TAG_RE } from "../pages/ISSB/ISSB-helpers";
import { foundationFigureText, foundationNumber } from "./foundationCitation";

/**
 * MUST equal `_FOUNDATION_ID_NAMESPACE` in backend-python/esgai/data/foundation_data.py (and
 * `FoundationCitationRewriter` in the Java backend, which moves citations on approval), so a figure cited by
 * hand and by the AI gets the same slice id and is listed once.
 */
const FOUNDATION_ID_NAMESPACE = "6f9d4e1a-3c2b-4f7e-9a51-8d0c7b6e2f43";

/** The citable id of one VERSION of a datapoint - mirrors Python's `slice_id_for`. */
export function foundationSliceId(datapointUid, version) {
  return uuidv5(`${datapointUid}:${version}`, FOUNDATION_ID_NAMESPACE);
}

/**
 * The marker attributes for one approved figure.
 * @param {object} row a FoundationCuratedPointDto from `/years/{year}/curated`
 * @param {number} year the reporting year it was picked from
 */
export function foundationCiteAttrs(row, year) {
  return {
    title: row.dataKey,
    "data-foundation_uid": row.datapointUid,
    "data-foundation_version": String(foundationNumber(row.version) ?? 1),
    "data-foundation_year": String(year),
    "data-curated_point_id": row.id == null ? null : String(row.id),
    "data-foundation_figure": foundationFigureText(row) || null,
  };
}

/** The visible text of a foundation citation: "59,721 tCO2e [1]", or "[1]" for a figure with no value. */
export function foundationCiteText(row, number) {
  const figure = foundationFigureText(row);
  return figure ? `${figure} [${number}]` : `[${number}]`;
}

function citeAttrsOf(attrStr) {
  const attrs = {};
  const re = new RegExp(CITE_ATTR_RE.source, CITE_ATTR_RE.flags);
  let m;
  while ((m = re.exec(attrStr || "")) !== null) {
    const [, key, a, b, c, d] = m;
    attrs[key.trim().toLowerCase()] = (a ?? b ?? c ?? d ?? "").trim();
  }
  return attrs;
}

function decodeEntities(text) {
  return (text || "")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
}

/**
 * Every hand-cited foundation figure in an answer, as `source_slices` entries, one per datapoint version.
 * @param {string} html the answer
 * @returns {object[]}
 */
export function foundationLinksFromHtml(html) {
  const links = [];
  const seen = new Set();
  const re = new RegExp(CITE_TAG_RE.source, CITE_TAG_RE.flags);
  let m;
  while ((m = re.exec(html || "")) !== null) {
    const attrs = citeAttrsOf(m[1]);
    const uid = attrs["data-foundation_uid"];
    if (!uid) continue;
    const version = foundationNumber(attrs["data-foundation_version"]) ?? 1;
    const id = foundationSliceId(uid, version);
    if (seen.has(id)) continue;
    seen.add(id);
    const year = foundationNumber(attrs["data-foundation_year"]);
    links.push({
      id,
      title: year == null ? "Foundation Data" : `Foundation Data ${year}`,
      foundationDatapointUid: uid,
      foundationVersion: version,
      foundationYear: year,
      curatedPointId: foundationNumber(attrs["data-curated_point_id"]),
      dataKey: attrs.title ? decodeEntities(attrs.title) : null,
      citedInline: true,
    });
  }
  return links;
}

/**
 * The `sourceSlices` a manual save should send, or undefined to leave the question's sources untouched.
 *
 * Entries flagged `citedInline` - by this editor or by the AI, which writes the same citation - are owned by
 * the text: kept while their citation is still there, dropped when it is gone, and added for a new one. Every
 * other entry is kept as it is. A citation of a figure already listed (same slice id) is not listed twice.
 *
 * @param {object[]|null|undefined} existingSlices question.sourceSlices as loaded
 * @param {string} html the answer being saved
 * @returns {object[]|undefined}
 */
export function sourceSlicesForManualSave(existingSlices, html) {
  const existing = existingSlices || [];
  const inText = new Map(foundationLinksFromHtml(html).map((link) => [link.id, link]));
  const next = [];
  const seen = new Set();
  for (const slice of existing) {
    if (slice?.citedInline && !inText.has(slice.id)) continue; // its citation was deleted
    next.push(slice);
    if (slice?.id) seen.add(slice.id);
  }
  for (const [id, link] of inText) {
    if (!seen.has(id)) next.push(link);
  }
  const unchanged = next.length === existing.length && next.every((slice, i) => slice === existing[i]);
  return unchanged ? undefined : next;
}

/**
 * The number to show inside a new marker: the one this datapoint already has in the text, else the next free.
 * Numbered by the same identity rule as `applyCitationCounter`, so a later renumber leaves it where it is.
 */
export function nextCitationNumber(html, datapointUid) {
  const numbers = new Map();
  const re = new RegExp(CITE_TAG_RE.source, CITE_TAG_RE.flags);
  let m;
  while ((m = re.exec(html || "")) !== null) {
    const attrs = citeAttrsOf(m[1]);
    const identity =
      attrs["data-source_id"] || attrs["data-question_id"] || attrs["data-url"] || attrs["data-foundation_uid"];
    if (identity && !numbers.has(identity)) numbers.set(identity, numbers.size + 1);
  }
  return numbers.get(datapointUid) ?? numbers.size + 1;
}

const escapeAttr = (value) =>
  String(value).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * The answer with every foundation citation of an older version moved onto its datapoint's LATEST approved
 * row: the tag's version / curated-point id / figure attributes, and the figure shown in its text, are
 * rewritten; the citation number and every other attribute stay as they were.
 *
 * Only the figure INSIDE the citation changes — prose around it that restates the number ("up 4% on last
 * year") is the reader's to check, which is why this goes into the editor for review rather than saving.
 *
 * The Java `FoundationCitationRewriter` applies the same rewrite server-side for "Manage changed data" options
 * 3 and 4 — keep the two in step.
 *
 * A datapoint with no latest row (deleted, or its year failed to load) is left alone rather than guessed.
 *
 * @param {string} html the answer
 * @param {Object<string, object>} latestByUid datapointUid → FoundationCuratedPointDto (current version)
 * @returns {{ html: string, updated: number }} the new answer, and how many citations changed
 */
export function updateFoundationCitesToLatest(html, latestByUid) {
  let updated = 0;
  const next = (html || "").replace(new RegExp(CITE_TAG_RE.source, CITE_TAG_RE.flags), (match, attrStr, inner) => {
    const attrs = citeAttrsOf(attrStr);
    const uid = attrs["data-foundation_uid"];
    const latest = uid ? latestByUid?.[uid] : null;
    if (!latest) return match;
    const latestVersion = foundationNumber(latest.version);
    if (latestVersion == null || foundationNumber(attrs["data-foundation_version"]) === latestVersion) return match;
    const figure = foundationFigureText(latest);
    const replacements = {
      "data-foundation_version": String(latestVersion),
      "data-curated_point_id": latest.id == null ? null : String(latest.id),
      "data-foundation_figure": figure || null,
    };
    let rebuilt = attrStr;
    for (const [key, value] of Object.entries(replacements)) {
      const attrRe = new RegExp(`\\s*${key}=("[^"]*"|'[^']*')`, "i");
      if (value == null) rebuilt = rebuilt.replace(attrRe, "");
      else if (attrRe.test(rebuilt)) rebuilt = rebuilt.replace(attrRe, ` ${key}="${escapeAttr(value)}"`);
      else rebuilt = `${rebuilt} ${key}="${escapeAttr(value)}"`;
    }
    const number = (inner.match(/\[(\d+)\]\s*$/) || [])[1];
    const text = number ? (figure ? `${escapeAttr(figure)} [${number}]` : `[${number}]`) : inner;
    updated += 1;
    return `<cite ${rebuilt.trim()}>${text}</cite>`;
  });
  return { html: next, updated };
}
